import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Tabs,
  Table,
  Input,
  Button,
  Upload,
  Popconfirm,
  Select,
  Tag,
  Tooltip,
  notification,
  Space,
  Modal,
  Badge,
  Empty,
  Spin
} from "antd";
import {
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileUnknownOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  GlobalOutlined
} from "@ant-design/icons";
import { getReq, postReq, deleteApi } from "../api";
import "./DmsPage.css";

const { Title, Text } = Typography;
const { Option } = Select;

export default function DmsPage() {
  const currentUser = JSON.parse(localStorage.getItem("userDetails") || '{"name": "Admin User"}');

  // States
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [clientDocs, setClientDocs] = useState([]);
  const [vendorDocs, setVendorDocs] = useState([]);
  const [candidateDocs, setCandidateDocs] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // Pagination States
  const [companyPage, setCompanyPage] = useState(1);
  const [companyTotal, setCompanyTotal] = useState(0);
  const [clientPage, setClientPage] = useState(1);
  const [clientTotal, setClientTotal] = useState(0);
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidateTotal, setCandidateTotal] = useState(0);
  const pageSize = 8;

  // Search Filter State (Company tab)
  const [companySearch, setCompanySearch] = useState("");


  // Loading States
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [loadingCandidate, setLoadingCandidate] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Modal Visibility States
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);

  // Form values - Company Modal
  const [companyDocName, setCompanyDocName] = useState("");
  const [companyFile, setCompanyFile] = useState(null);
  const [companyFileList, setCompanyFileList] = useState([]);

  // Form values - Client Modal
  const [clientDocName, setClientDocName] = useState("");
  const [clientFile, setClientFile] = useState(null);
  const [clientFileList, setClientFileList] = useState([]);

  // Form values - Vendor Modal
  const [vendorDocName, setVendorDocName] = useState("");
  const [vendorFile, setVendorFile] = useState(null);
  const [vendorFileList, setVendorFileList] = useState([]);

  // Form values - Candidate Modal
  const [candidateDocName, setCandidateDocName] = useState("");
  const [candidateFile, setCandidateFile] = useState(null);
  const [candidateFileList, setCandidateFileList] = useState([]);

  // Fetch all dropdown clients
  const fetchClients = async () => {
    setLoadingClients(true);
    const userRole = localStorage.getItem("userRole");
    const endpoint = userRole === "company" ? "company/clients/dropdown" : "common/clients/dropdown";
    try {
      const res = await postReq(endpoint, {});
      if (res && res.status === 200) {
        // Map from { value, label } structure returned by dropdown endpoint
        setClients(res.data || []);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load clients.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error("Failed to load clients:", err);
    } finally {
      setLoadingClients(false);
    }
  };

  // Fetch all dropdown vendors
  const fetchVendors = async () => {
    setLoadingVendors(true);
    const userRole = localStorage.getItem("userRole");
    const endpoint = userRole === "company" ? "company/vendors/dropdown" : "common/vendors/dropdown";
    try {
      const res = await postReq(endpoint, {});
      if (res && res.status === 200) {
        setVendors(res.data || []);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load vendors.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error("Failed to load vendors:", err);
    } finally {
      setLoadingVendors(false);
    }
  };

  // Fetch all dropdown candidates
  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    const userRole = localStorage.getItem("userRole");
    const endpoint = userRole === "company" ? "company/candidate/dropdown" : "common/candidate/dropdown";
    try {
      const res = await postReq(endpoint, {});
      if (res && res.status === 200) {
        setCandidates(res.data || []);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load candidates.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error("Failed to load candidates:", err);
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Fetch corporate documents
  const fetchCompanyDocs = async (page = 1, searchStr = "") => {
    setLoadingCompany(true);
    try {
      const url = `dms?category=company&page=${page}&limit=${pageSize}&search=${encodeURIComponent(searchStr)}`;
      const res = await getReq(url);
      if (res && res.status === 200) {
        let docs = [];
        let totalVal = 0;
        if (Array.isArray(res.data)) {
          docs = res.data;
          totalVal = res.data.length;
        } else if (res.data && Array.isArray(res.data.documents)) {
          docs = res.data.documents;
          totalVal = res.data.total || 0;
        }
        setCompanyDocs(docs);
        setCompanyTotal(totalVal);
        setCompanyPage(page);
      }
    } catch (err) {
      console.error("Failed to load company documents:", err);
    } finally {
      setLoadingCompany(false);
    }
  };

  // Fetch client documents
  const fetchClientDocs = async (clientId, page = 1) => {
    setLoadingClient(true);
    try {
      let url = `dms?category=client&page=${page}&limit=${pageSize}`;
      if (clientId) {
        url += `&clientId=${clientId}`;
      }
      const res = await getReq(url);
      if (res && res.status === 200) {
        let docs = [];
        let totalVal = 0;
        if (Array.isArray(res.data)) {
          docs = res.data;
          totalVal = res.data.length;
        } else if (res.data && Array.isArray(res.data.documents)) {
          docs = res.data.documents;
          totalVal = res.data.total || 0;
        }
        setClientDocs(docs);
        setClientTotal(totalVal);
        setClientPage(page);
      }
    } catch (err) {
      console.error("Failed to load client documents:", err);
    } finally {
      setLoadingClient(false);
    }
  };

  // Fetch vendor documents
  const fetchVendorDocs = async (vendorId, page = 1) => {
    setLoadingVendor(true);
    try {
      let url = `dms?category=vendor&page=${page}&limit=${pageSize}`;
      if (vendorId) {
        url += `&vendorId=${vendorId}`;
      }
      const res = await getReq(url);
      if (res && res.status === 200) {
        let docs = [];
        let totalVal = 0;
        if (Array.isArray(res.data)) {
          docs = res.data;
          totalVal = res.data.length;
        } else if (res.data && Array.isArray(res.data.documents)) {
          docs = res.data.documents;
          totalVal = res.data.total || 0;
        }
        setVendorDocs(docs);
        setVendorTotal(totalVal);
        setVendorPage(page);
      }
    } catch (err) {
      console.error("Failed to load vendor documents:", err);
    } finally {
      setLoadingVendor(false);
    }
  };

  // Fetch candidate documents
  const fetchCandidateDocs = async (candidateId, page = 1) => {
    setLoadingCandidate(true);
    try {
      let url = `dms?category=candidate&page=${page}&limit=${pageSize}`;
      if (candidateId) {
        url += `&candidateId=${candidateId}`;
      }
      const res = await getReq(url);
      if (res && res.status === 200) {
        let docs = [];
        let totalVal = 0;
        if (Array.isArray(res.data)) {
          docs = res.data;
          totalVal = res.data.length;
        } else if (res.data && Array.isArray(res.data.documents)) {
          docs = res.data.documents;
          totalVal = res.data.total || 0;
        }
        setCandidateDocs(docs);
        setCandidateTotal(totalVal);
        setCandidatePage(page);
      }
    } catch (err) {
      console.error("Failed to load candidate documents:", err);
    } finally {
      setLoadingCandidate(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchClients();
    fetchVendors();
    fetchCandidates();
  }, []);

  // Debounced search for Company Docs
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCompanyDocs(1, companySearch);
    }, 400);
    return () => clearTimeout(handler);
  }, [companySearch]);

  useEffect(() => {
    fetchClientDocs(selectedClientId, 1);
  }, [selectedClientId]);

  useEffect(() => {
    fetchVendorDocs(selectedVendorId, 1);
  }, [selectedVendorId]);

  useEffect(() => {
    fetchCandidateDocs(selectedCandidateId, 1);
  }, [selectedCandidateId]);

  // Helper formatting logic
  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase() || "";
    if (type.includes("pdf")) {
      return <span className="doc-icon-wrapper doc-icon-pdf"><FilePdfOutlined /></span>;
    }
    if (type.includes("word") || type.includes("document") || type.includes("docx") || type.includes("doc")) {
      return <span className="doc-icon-wrapper doc-icon-word"><FileWordOutlined /></span>;
    }
    if (type.includes("sheet") || type.includes("excel") || type.includes("spreadsheet") || type.includes("xlsx") || type.includes("xls")) {
      return <span className="doc-icon-wrapper doc-icon-excel"><FileExcelOutlined /></span>;
    }
    return <span className="doc-icon-wrapper doc-icon-other"><FileUnknownOutlined /></span>;
  };

  const formatSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes >= 1048576) {
      return `${(bytes / 1048576).toFixed(2)} MB`;
    }
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Live download opener
  const handleDownload = (doc) => {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    let downloadUrl = "";
    if (apiUrl.includes("localhost")) {
      downloadUrl = `http://localhost:9003/files/${doc.fileName}`;
    } else {
      downloadUrl = `https://rmt.cloudstakes.com/api/files/${doc.fileName}`;
    }
    window.open(downloadUrl, "_blank");
    notification.success({
      message: "Downloading File",
      description: `Opening ${doc.fileName} in new tab.`,
      placement: "topRight"
    });
  };

  // Upload Actions
  const handleUploadCompany = async () => {
    if (!companyDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!companyFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", companyDocName.trim());
    formData.append("category", "company");
    formData.append("file", companyFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${companyDocName}" has been added to Company Documents.`,
          placement: "topRight"
        });
        setCompanyDocName("");
        setCompanyFile(null);
        setCompanyFileList([]);
        setCompanyModalOpen(false);
        fetchCompanyDocs(1, companySearch);
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClient = async () => {
    if (!selectedClientId) {
      notification.error({
        message: "Error",
        description: "Please select a client before uploading.",
        placement: "topRight"
      });
      return;
    }
    if (!clientDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!clientFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", clientDocName.trim());
    formData.append("category", "client");
    formData.append("clientId", selectedClientId);
    formData.append("file", clientFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${clientDocName}" has been added for this client.`,
          placement: "topRight"
        });
        setClientDocName("");
        setClientFile(null);
        setClientFileList([]);
        setClientModalOpen(false);
        fetchClientDocs(selectedClientId, 1);
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadCandidate = async () => {
    if (!selectedCandidateId) {
      notification.error({
        message: "Error",
        description: "Please select a candidate before uploading.",
        placement: "topRight"
      });
      return;
    }
    if (!candidateDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!candidateFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", candidateDocName.trim());
    formData.append("category", "candidate");
    formData.append("candidateId", selectedCandidateId);
    formData.append("file", candidateFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${candidateDocName}" has been added for this candidate.`,
          placement: "topRight"
        });
        setCandidateDocName("");
        setCandidateFile(null);
        setCandidateFileList([]);
        setCandidateModalOpen(false);
        fetchCandidateDocs(selectedCandidateId, 1);
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadVendor = async () => {
    if (!selectedVendorId) {
      notification.error({
        message: "Error",
        description: "Please select a vendor before uploading.",
        placement: "topRight"
      });
      return;
    }
    if (!vendorDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!vendorFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", vendorDocName.trim());
    formData.append("category", "vendor");
    formData.append("vendorId", selectedVendorId);
    formData.append("file", vendorFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${vendorDocName}" has been added for this vendor.`,
          placement: "topRight"
        });
        setVendorDocName("");
        setVendorFile(null);
        setVendorFileList([]);
        setVendorModalOpen(false);
        fetchVendorDocs(selectedVendorId, 1);
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  // Delete Actions
  const handleDeleteCompany = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Company document removed successfully.",
          placement: "topRight"
        });
        fetchCompanyDocs(companyPage, companySearch);
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Client document removed successfully.",
          placement: "topRight"
        });
        fetchClientDocs(selectedClientId, clientPage);
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const handleDeleteVendor = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Vendor document removed successfully.",
          placement: "topRight"
        });
        fetchVendorDocs(selectedVendorId, vendorPage);
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const handleDeleteCandidate = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Candidate document removed successfully.",
          placement: "topRight"
        });
        fetchCandidateDocs(selectedCandidateId, candidatePage);
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const activeClientInfo = clients.find((c) => c.value === selectedClientId);
  const activeVendorInfo = vendors.find((v) => v.value === selectedVendorId);
  const activeCandidateInfo = candidates.find((c) => c.value === selectedCandidateId);

  // Filter company documents list
  const filteredCompanyDocs = companyDocs.filter(
    (d) =>
      d.name.toLowerCase().includes(companySearch.toLowerCase()) ||
      d.fileName.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Table Columns
  const companyColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="blue">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={<DownloadOutlined style={{
                color: "#13487d"
              }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteCompany(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                danger
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const clientColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Client",
      dataIndex: "clientId",
      key: "client",
      render: (client) => {
        return client?.clientCompanyName ? (
          <Tag color="geekblue">{client.clientCompanyName}</Tag>
        ) : (
          "—"
        );
      }
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="cyan">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={<DownloadOutlined style={{ color: "#13487d" }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteClient(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const vendorColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Vendor",
      dataIndex: "vendorId",
      key: "vendor",
      render: (vendor) => {
        return vendor?.companyName ? (
          <Tag color="geekblue">{vendor.companyName}</Tag>
        ) : (
          "—"
        );
      }
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="cyan">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={<DownloadOutlined style={{ color: "#13487d" }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteVendor(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const candidateColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Candidate",
      dataIndex: "candidateId",
      key: "candidate",
      render: (candidate) => {
        return candidate?.name ? (
          <Tag color="geekblue">{candidate.name}</Tag>
        ) : (
          "—"
        );
      }
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="cyan">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={<DownloadOutlined style={{ color: "#13487d" }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteCandidate(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="dms-container">
      <div className="dms-card">
        {/* Header */}
        <div className="dms-header">
          <div>
            <Title className="dms-header-title">Document Management System</Title>
            <span className="dms-header-subtitle">
              Securely store, organize, and download corporate policy documents and client contracts
            </span>
          </div>
        </div>

        {/* Tab Wrapper */}
        <Tabs defaultActiveKey="1" className="dms-tabs">
          {/* Tab 1: Company Documents */}
          <Tabs.TabPane
            tab={
              <span>
                Company Documents{" "}
                <Badge
                  count={companyTotal}
                  style={{ backgroundColor: "#13487d", marginLeft: 6 }}
                  showZero
                />
              </span>
            }
            key="1"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>

              {/* Controls Row: Search + Upload Trigger */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Input
                  placeholder="Search corporate documents by name..."
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  style={{ width: 320 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setCompanyModalOpen(true)}
                  className="btn-premium"
                >
                  Upload Corporate Document
                </Button>
              </div>

              {/* Table List */}
              <div className="dms-table-container">
                <Table
                  dataSource={companyDocs}
                  columns={companyColumns}
                  rowKey="_id"
                  className="dms-table"
                  pagination={{
                    current: companyPage,
                    pageSize: pageSize,
                    total: companyTotal,
                    onChange: (page) => fetchCompanyDocs(page, companySearch),
                    showSizeChanger: false,
                  }}
                  loading={loadingCompany}
                  scroll={{ x: "max-content" }}
                />
              </div>
            </div>
          </Tabs.TabPane>

          {/* Tab 2: Client Documents */}
          <Tabs.TabPane
            tab={
              <span>
                Client Documents{" "}
                <Badge
                  count={clientTotal}
                  style={{ backgroundColor: "#64748b", marginLeft: 6 }}
                  showZero
                />
              </span>
            }
            key="2"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>
              {/* Client Dropdown Bar */}
              <div className="dms-client-selector-bar">
                <span className="dms-client-label">Select Workspace Client:</span>
                <Select
                  showSearch
                  allowClear
                  placeholder="Search and select client company..."
                  optionFilterProp="children"
                  className="dms-client-select"
                  onChange={(val) => {
                    setSelectedClientId(val || null);
                    setClientDocName("");
                    setClientFile(null);
                    setClientFileList([]);
                  }}
                  value={selectedClientId}
                  loading={loadingClients}
                >
                  {clients.map((c) => (
                    <Option key={c.value} value={c.value}>
                      {c.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Main Content Area */}
              <div>
                {/* Client Info Banner */}
                <div className="client-info-header">
                  <div>
                    <h4 className="client-info-name">
                      {selectedClientId ? activeClientInfo?.label : "All Clients"}
                    </h4>
                    {selectedClientId && (
                      <div className="client-info-meta">
                        <span>
                          <GlobalOutlined style={{ marginRight: 4 }} />
                          <a href={`https://${activeClientInfo?.website}`} target="_blank" rel="noreferrer">
                            {activeClientInfo?.website || "Website"}
                          </a>
                        </span>
                      </div>
                    )}
                  </div>
                  <Space size="middle">
                    <Tag color="purple" style={{ margin: 0, padding: "4px 10px", fontSize: "13px", fontWeight: 600 }}>
                      {clientTotal} Documents
                    </Tag>
                    {selectedClientId && (
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setClientModalOpen(true)}
                        className="btn-premium"
                      >
                        Upload Client Document
                      </Button>
                    )}
                  </Space>
                </div>

                {/* Document Table */}
                {clientDocs.length === 0 && !loadingClient ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      selectedClientId
                        ? `No documents uploaded for ${activeClientInfo?.label} yet.`
                        : "No client documents uploaded yet."
                    }
                    style={{ margin: "48px 0" }}
                  />
                ) : (
                  <div className="dms-table-container">
                    <Table
                      dataSource={clientDocs}
                      columns={clientColumns}
                      rowKey="_id"
                      className="dms-table"
                      pagination={{
                        current: clientPage,
                        pageSize: pageSize,
                        total: clientTotal,
                        onChange: (page) => fetchClientDocs(selectedClientId, page),
                        showSizeChanger: false,
                      }}
                      loading={loadingClient}
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Tabs.TabPane>

          {/* Tab: Vendor Documents */}
          <Tabs.TabPane
            tab={
              <span>
                Vendor Documents{" "}
                <Badge
                  count={vendorTotal}
                  style={{ backgroundColor: "#0284c7", marginLeft: 6 }}
                  showZero
                />
              </span>
            }
            key="vendor-tab"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>
              {/* Vendor Dropdown Bar */}
              <div className="dms-client-selector-bar">
                <span className="dms-client-label">Select Workspace Vendor:</span>
                <Select
                  showSearch
                  allowClear
                  placeholder="Search and select vendor company..."
                  optionFilterProp="children"
                  className="dms-client-select"
                  onChange={(val) => {
                    setSelectedVendorId(val || null);
                    setVendorDocName("");
                    setVendorFile(null);
                    setVendorFileList([]);
                  }}
                  value={selectedVendorId}
                  loading={loadingVendors}
                >
                  {vendors.map((v) => (
                    <Option key={v.value} value={v.value}>
                      {v.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Main Content Area */}
              <div>
                {/* Vendor Info Banner */}
                <div className="client-info-header">
                  <div>
                    <h4 className="client-info-name">
                      {selectedVendorId ? activeVendorInfo?.label : "All Vendors"}
                    </h4>
                  </div>
                  <Space size="middle">
                    <Tag color="purple" style={{ margin: 0, padding: "4px 10px", fontSize: "13px", fontWeight: 600 }}>
                      {vendorTotal} Documents
                    </Tag>
                    {selectedVendorId && (
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setVendorModalOpen(true)}
                        className="btn-premium"
                      >
                        Upload Vendor Document
                      </Button>
                    )}
                  </Space>
                </div>

                {/* Document Table */}
                {vendorDocs.length === 0 && !loadingVendor ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      selectedVendorId
                        ? `No documents uploaded for ${activeVendorInfo?.label} yet.`
                        : "No vendor documents uploaded yet."
                    }
                    style={{ margin: "48px 0" }}
                  />
                ) : (
                  <div className="dms-table-container">
                    <Table
                      dataSource={vendorDocs}
                      columns={vendorColumns}
                      rowKey="_id"
                      className="dms-table"
                      pagination={{
                        current: vendorPage,
                        pageSize: pageSize,
                        total: vendorTotal,
                        onChange: (page) => fetchVendorDocs(selectedVendorId, page),
                        showSizeChanger: false,
                      }}
                      loading={loadingVendor}
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Tabs.TabPane>

          {/* Tab 3: Candidate Documents */}
          <Tabs.TabPane
            tab={
              <span>
                Candidate Documents{" "}
                <Badge
                  count={candidateTotal}
                  style={{ backgroundColor: "#2563eb", marginLeft: 6 }}
                  showZero
                />
              </span>
            }
            key="3"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>
              {/* Candidate Dropdown Bar */}
              <div className="dms-client-selector-bar">
                <span className="dms-client-label">Select Candidate:</span>
                <Select
                  showSearch
                  allowClear
                  placeholder="Search and select candidate..."
                  optionFilterProp="children"
                  className="dms-client-select"
                  onChange={(val) => {
                    setSelectedCandidateId(val || null);
                    setCandidateDocName("");
                    setCandidateFile(null);
                    setCandidateFileList([]);
                  }}
                  value={selectedCandidateId}
                  loading={loadingCandidates}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {candidates.map((c) => (
                    <Option key={c.value} value={c.value}>
                      {c.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Main Content Area */}
              <div>
                {/* Candidate Info Banner */}
                <div className="client-info-header">
                  <div>
                    <h4 className="client-info-name">
                      {selectedCandidateId ? activeCandidateInfo?.label : "All Candidates"}
                    </h4>
                  </div>
                  <Space size="middle">
                    <Tag color="purple" style={{ margin: 0, padding: "4px 10px", fontSize: "13px", fontWeight: 600 }}>
                      {candidateTotal} Documents
                    </Tag>
                    {selectedCandidateId && (
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setCandidateModalOpen(true)}
                        className="btn-premium"
                      >
                        Upload Candidate Document
                      </Button>
                    )}
                  </Space>
                </div>

                {/* Document Table */}
                {candidateDocs.length === 0 && !loadingCandidate ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      selectedCandidateId
                        ? `No documents uploaded for ${activeCandidateInfo?.label} yet.`
                        : "No candidate documents uploaded yet."
                    }
                    style={{ margin: "48px 0" }}
                  />
                ) : (
                  <div className="dms-table-container">
                    <Table
                      dataSource={candidateDocs}
                      columns={candidateColumns}
                      rowKey="_id"
                      className="dms-table"
                      pagination={{
                        current: candidatePage,
                        pageSize: pageSize,
                        total: candidateTotal,
                        onChange: (page) => fetchCandidateDocs(selectedCandidateId, page),
                        showSizeChanger: false,
                      }}
                      loading={loadingCandidate}
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>

        {/* ── Modals ── */}

        {/* Company Upload Modal */}
        <Modal
          title={<span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>Upload Corporate Document</span>}
          open={companyModalOpen}
          onCancel={() => {
            setCompanyModalOpen(false);
            setCompanyDocName("");
            setCompanyFile(null);
            setCompanyFileList([]);
          }}
          onOk={handleUploadCompany}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Employee Handbook 2026"
                value={companyDocName}
                onChange={(e) => setCompanyDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={companyFileList}
                beforeUpload={(file) => {
                  setCompanyFile(file);
                  setCompanyFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setCompanyFile(null);
                  setCompanyFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Client Upload Modal */}
        <Modal
          title={
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
              Upload Document for {activeClientInfo?.label}
            </span>
          }
          open={clientModalOpen}
          onCancel={() => {
            setClientModalOpen(false);
            setClientDocName("");
            setClientFile(null);
            setClientFileList([]);
          }}
          onOk={handleUploadClient}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Master Services Agreement"
                value={clientDocName}
                onChange={(e) => setClientDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={clientFileList}
                beforeUpload={(file) => {
                  setClientFile(file);
                  setClientFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setClientFile(null);
                  setClientFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Vendor Upload Modal */}
        <Modal
          title={
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
              Upload Document for {activeVendorInfo?.label}
            </span>
          }
          open={vendorModalOpen}
          onCancel={() => {
            setVendorModalOpen(false);
            setVendorDocName("");
            setVendorFile(null);
            setVendorFileList([]);
          }}
          onOk={handleUploadVendor}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Master Services Agreement"
                value={vendorDocName}
                onChange={(e) => setVendorDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={vendorFileList}
                beforeUpload={(file) => {
                  setVendorFile(file);
                  setVendorFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setVendorFile(null);
                  setVendorFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Candidate Upload Modal */}
        <Modal
          title={
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
              Upload Document for {activeCandidateInfo?.label}
            </span>
          }
          open={candidateModalOpen}
          onCancel={() => {
            setCandidateModalOpen(false);
            setCandidateDocName("");
            setCandidateFile(null);
            setCandidateFileList([]);
          }}
          onOk={handleUploadCandidate}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Resume or Certification"
                value={candidateDocName}
                onChange={(e) => setCandidateDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={candidateFileList}
                beforeUpload={(file) => {
                  setCandidateFile(file);
                  setCandidateFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setCandidateFile(null);
                  setCandidateFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

      </div>
    </div>
  );
}
